import { Answer } from 'types/answers'
import * as cache from 'state/cache'
import { normalizeQuestAncestors } from 'state/questAncestors'
import { answerHasPrivateEmbedPlaceholder } from 'state/viewerNeutralAnswerUpdate'
import { answerEmbedsHavePermalinkTargets } from 'utils/shareUtils'

type GetAnswers = (answerIds: string[]) => Promise<Answer[]>

type HydrationOptions = {
    signal?: AbortSignal | null
    streamPreview?: boolean
    viewerNeutral?: boolean
    deferAnswerHydration?: boolean
}

/**
 * Coalesces the per-quest answer fetches a stream page issues in the same tick
 * into one bulk request. Hydrating a page runs ~20 quest hydrations in
 * parallel, and each used to POST its own /answers/bulk — the page render
 * waited on the slowest of N round trips, and TOP NEW pages (threads the
 * viewer has by definition never cached) always paid all N. Batching keeps
 * per-quest hydration semantics: every caller receives exactly the answers it
 * requested, and a caller arriving after the batch fires starts a new one.
 */
export function createCoalescedGetAnswers(getAnswers: GetAnswers): GetAnswers {
    let sharedIds: Set<string> | null = null
    let sharedPromise: Promise<Map<string, Answer>> | null = null

    return async (answerIds: string[]) => {
        const requestedIds = (answerIds || []).filter(Boolean).map(String)
        if (requestedIds.length === 0) return []

        let batchIds = sharedIds
        let batchPromise = sharedPromise
        if (!batchIds || !batchPromise) {
            const newIds = new Set<string>()
            const newPromise = (async () => {
                // Yield one macrotask so every hydration kicked off for this
                // page joins the batch before it fires.
                await new Promise(resolve => setTimeout(resolve, 0))
                if (sharedIds === newIds) {
                    sharedIds = null
                    sharedPromise = null
                }
                const answers = await getAnswers(Array.from(newIds))
                const answersById = new Map<string, Answer>()
                answers.forEach(answer => {
                    if (answer?.id) answersById.set(String(answer.id), answer)
                })
                return answersById
            })()
            sharedIds = newIds
            sharedPromise = newPromise
            batchIds = newIds
            batchPromise = newPromise
        }

        requestedIds.forEach(id => batchIds.add(id))
        const answersById = await batchPromise
        return requestedIds
            .map(id => answersById.get(id))
            .filter((answer): answer is Answer => Boolean(answer))
    }
}

const STREAM_PREVIEW_ANSWER_COUNT = 3

export function selectStreamPreviewAnswerIds(quest): string[] {
    const sortedAnswerIds = Array.isArray(quest?.sorted_answer_ids)
        ? quest.sorted_answer_ids.filter(Boolean).map(String)
        : []
    const feedCard = quest?.feed_card
    if (
        feedCard?.version === 1 &&
        feedCard?.availability === 'available' &&
        typeof feedCard?.projection_fingerprint === 'string' &&
        feedCard.projection_fingerprint.length > 0 &&
        Array.isArray(feedCard?.preview_answer_ids)
    ) {
        const sortedAnswerIdSet = new Set(sortedAnswerIds)
        const projectedAnswerIds: string[] = Array.from(
            new Set<string>(feedCard.preview_answer_ids.filter(Boolean).map(String)),
        )
        if (projectedAnswerIds.every(answerId => sortedAnswerIdSet.has(answerId))) {
            return projectedAnswerIds
        }
    }
    const matchingAnswerIds = Array.isArray(quest?.matching_answer_ids)
        ? quest.matching_answer_ids.filter(Boolean).map(String)
        : []

    if (matchingAnswerIds.length > 0) {
        const sortedAnswerIdSet = new Set(sortedAnswerIds)
        return matchingAnswerIds
            .filter(answerId => sortedAnswerIdSet.has(answerId))
            .slice(0, STREAM_PREVIEW_ANSWER_COUNT)
    }

    return sortedAnswerIds.slice(-STREAM_PREVIEW_ANSWER_COUNT)
}

function throwIfHydrationAborted(signal?: AbortSignal | null) {
    if (!signal?.aborted) return
    if (signal.reason instanceof Error) throw signal.reason

    const error = new Error('Quest hydration aborted')
    error.name = 'AbortError'
    throw error
}

function isHydratedQuestAnswer(answer: unknown): boolean {
    return (
        cache.isViewerHydratedAnswerCacheEntry(answer as Record<string, unknown>) &&
        answerEmbedsHavePermalinkTargets(answer)
    )
}

/**
 * Hydrates a quest object whose answer payload may contain only answer ids.
 *
 * Stream endpoints request `answer_payload=ids`, so a cold cache needs a bulk
 * answer fetch before the quest can render real title/reply content.
 */
export async function hydrateAnswersIntoQuestWithFetcher(
    quest,
    getAnswers: GetAnswers,
    { signal, streamPreview = false, viewerNeutral = false }: HydrationOptions = {},
) {
    throwIfHydrationAborted(signal)
    if (!quest) return null
    const cachedQuestFallback = quest?.id ? cache.getCachedQuest(String(quest.id), new Set()) : null

    const serverSortedAnswers = Array.isArray(quest.sorted_answers)
        ? quest.sorted_answers.filter(Boolean)
        : []
    const serverSortedAnswersById = new Map(
        serverSortedAnswers.filter(answer => answer?.id).map(answer => [String(answer.id), answer]),
    )
    const serverParent = quest.parent || null
    const serverGrandParent = quest.grand_parent || null

    const serverHydratedAnswers = [...serverSortedAnswers, serverParent, serverGrandParent].filter(
        Boolean,
    )

    const answersById = new Map<string, Answer>()
    serverHydratedAnswers.forEach(answer => {
        if (answer?.id) answersById.set(String(answer.id), answer)
    })

    const previewAnswerIds = streamPreview ? selectStreamPreviewAnswerIds(quest) : null
    const questWithHydrationScope = {
        ...quest,
        __knovAnswerHydrationScope: streamPreview ? 'stream-preview' : 'full',
        __knovStreamPreviewAnswerIds: streamPreview ? previewAnswerIds : undefined,
    }

    const answerIdsToHydrate = Array.from(
        new Set(
            [
                ...(streamPreview
                    ? previewAnswerIds || []
                    : Array.isArray(quest.sorted_answer_ids)
                    ? quest.sorted_answer_ids
                    : []),
                ...(!streamPreview ? serverSortedAnswers.map(answer => answer?.id) : []),
                quest.parent_id,
                serverParent?.id,
                quest.grand_parent_id,
                serverGrandParent?.id,
            ]
                .filter(Boolean)
                .map(String)
                .filter(id => id !== '__PRIVATE__'),
        ),
    )

    const missingAnswerIds = answerIdsToHydrate.filter(answerId => {
        const serverAnswer = answersById.get(answerId)
        if (serverAnswer && viewerNeutral && answerHasPrivateEmbedPlaceholder(serverAnswer)) {
            return true
        }
        if (serverAnswer && isHydratedQuestAnswer(serverAnswer)) {
            return false
        }

        const cachedAnswer = cache.getCachedAnswer(answerId)
        if (!cachedAnswer) return true

        if (isHydratedQuestAnswer(cachedAnswer)) {
            answersById.set(answerId, cachedAnswer)
            return false
        }

        return true
    })

    let fetchedAnswers: Answer[] = []
    if (missingAnswerIds.length > 0) {
        fetchedAnswers = await getAnswers(missingAnswerIds)
        throwIfHydrationAborted(signal)
        fetchedAnswers.forEach(answer => {
            if (answer?.id) answersById.set(String(answer.id), answer)
        })
    }

    throwIfHydrationAborted(signal)
    // Commit answer entities only after the complete hydration request succeeds. This
    // preserves rich server embeds without letting abandoned requests mutate the cache.
    if (serverHydratedAnswers.length > 0 || fetchedAnswers.length > 0) {
        const fetchedAnswerIds = new Set(fetchedAnswers.map(answer => String(answer.id)))
        const serverAnswersToCache = viewerNeutral
            ? serverHydratedAnswers.filter(answer => !fetchedAnswerIds.has(String(answer.id)))
            : serverHydratedAnswers
        cache.cacheAnswers([...serverAnswersToCache, ...fetchedAnswers])
    }

    const normalizedQuest = normalizeQuestAncestors({
        quest: questWithHydrationScope,
        fallbackQuest: cachedQuestFallback,
        getAnswerById: answerId => {
            const normalizedId = String(answerId)
            return (
                answersById.get(normalizedId) ||
                serverSortedAnswersById.get(normalizedId) ||
                (serverParent?.id && String(serverParent.id) === normalizedId
                    ? serverParent
                    : null) ||
                (serverGrandParent?.id && String(serverGrandParent.id) === normalizedId
                    ? serverGrandParent
                    : null) ||
                cache.getCachedAnswer(normalizedId)
            )
        },
    })
    if (!normalizedQuest) return null
    quest = normalizedQuest

    throwIfHydrationAborted(signal)

    if (
        quest.__knovAnswerHydrationScope !== 'stream-preview' &&
        Array.isArray(quest.sorted_answer_ids) &&
        quest.sorted_answer_ids.length > 0
    ) {
        const hydratedCount = (quest.sorted_answers || []).filter(Boolean).length
        const expectedCount = quest.sorted_answer_ids.length
        if (hydratedCount < expectedCount) {
            const hydratedIdSet = new Set(
                (quest.sorted_answers || []).map(answer => String(answer.id)),
            )
            const missingAnswerIds = quest.sorted_answer_ids
                .map(id => String(id))
                .filter(id => !hydratedIdSet.has(id))
            console.warn('>>> hydrateAnswersIntoQuest: partial hydration detected', {
                questId: quest?.id,
                expectedCount,
                hydratedCount,
                missingCount: missingAnswerIds.length,
                missingAnswerIds,
            })
        }
    }

    return quest
}

export async function hydrateStreamPreviewIntoQuestWithFetcher(
    quest,
    getAnswers: GetAnswers,
    options: Omit<HydrationOptions, 'streamPreview'> = {},
) {
    return hydrateAnswersIntoQuestWithFetcher(quest, getAnswers, {
        ...options,
        streamPreview: true,
    })
}

export async function hydrateQuestForQueryWithFetcher(
    quest,
    getAnswers: GetAnswers,
    { signal, streamPreview = false, deferAnswerHydration = false }: HydrationOptions = {},
) {
    throwIfHydrationAborted(signal)
    if (!quest) return null

    // Targeted thread opens need the complete ordered membership before they can
    // choose a bounded render window, but fetching every post body here makes the
    // loading gate scale with the entire thread. Cache the ids-only show payload as
    // full membership and let the reveal hydrate just its head/target neighborhood.
    if (deferAnswerHydration) {
        return {
            ...quest,
            __knovAnswerHydrationScope: 'full' as const,
        }
    }

    return hydrateAnswersIntoQuestWithFetcher(quest, getAnswers, {
        signal,
        streamPreview,
    })
}
